import { Idea } from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { formatDate } from '@frontend/shared/util';
import { Divider, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import IdeaDetailAttachments from '../../idea-channel-detail/attachments';
import IdeaDetailBody from '../../idea-channel-detail/body';
import { DownOutlined } from '@ant-design/icons';
import { UpOutlined } from '@ant-design/icons';
import { MetricTag } from '@frontend/feature-idea-channel';
import useTranslation from 'next-translate/useTranslation';

const {Text} = Typography;

interface IdeaEvaluationDetailProps {
  idea: Idea;
  defaultCollapsed?: boolean;
}

function IdeaEvaluationDetail({
  idea,
  defaultCollapsed = false,
}: IdeaEvaluationDetailProps) {
  const { t } = useTranslation('common');
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const basicItems = [
    {
      title: t('topics'),
      content: idea.topics.map((topic) => topic.name).join(', '),
    },
    {
      title: t('business_area'),
      content: idea.businessAreas?.map((area) => area).join(', '),
    },
    {
      title: t('challenge'),
      content: idea.challenge?.title,
    },
    {
      title: t('country'),
      content: idea.countries.map((country) => country.name).join(', '),
    },
    {
      title: t('space'),
      content: idea.innovationSpaceTag?.name,
    },
  ];

  const TagItems = () => {
    if (idea.channelTag)
      return (
        <div className="flex gap-2 overflow-auto max-w-96">
          {idea.channelTag?.slice(0, 3).map((item, index) => {
            return (
              <div
                key={index}
                className="bg-gray-200 py-1 px-3 rounded-full text-xs text-nowrap"
              >
                <span>{item.name}</span>
              </div>
            );
          })}
          {idea.channelTag?.length > 3 && (
            <Tooltip
              title={idea.channelTag
                ?.slice(3)
                .map((i) => i.name)
                .join(', ')}
            >
              <div className="bg-gray-200 py-1 px-3 rounded-full text-xs text-nowrap">
                +{idea.channelTag?.length - 3}
              </div>
            </Tooltip>
          )}
        </div>
      );
    else return null;
  };

  const IdeaHeader = () => {
    return (
      <div className="flex gap-1 items-center mb-4 text-sm font-normal text-gray-900">
        <div className="flex items-center gap-2 max-w-56">
          <UserAvatar size={24} user={idea.createdBy} />
          <div className="truncate">{idea.createdBy.fullName}</div>
        </div>
        <Divider type="vertical" />
        <div className="font-normal text-nowrap">
          {formatDate(idea.createdAt, 'DD MMM YYYY, HH:mm')}
        </div>
        <Divider type="vertical" />
        <MetricTag tag={idea.metricTag} />
        {(idea.channelTag?.length || 0) > 0 && (
          <>
            <Divider type="vertical" />
            <TagItems />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="col-span-3 space-y-2">
        <IdeaHeader />
        <Text className="!text-gray-900 !font-semibold">{idea.name}</Text>
        <IdeaDetailBody
          idea={idea}
          size="small"
          collapsed={defaultCollapsed ? isCollapsed : true}
          showCollapse={!defaultCollapsed}
        />
        {!isCollapsed && <IdeaDetailAttachments idea={idea} />}
      </div>
      {!isCollapsed && (
        <>
          <Divider />
          <div className="space-y-3">
            {basicItems.map((item, index) => (
              <div key={index}>
                <div className="text-gray-700 font-medium text-xs">
                  {item.title}
                </div>
                <Text className="!text-gray-900 !text-sm">{item.content}</Text>
              </div>
            ))}
          </div>
        </>
      )}
      {defaultCollapsed && (
        <div>
          <span
            className="text-primary-600 cursor-pointer"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <>
                <DownOutlined /> {t('see_more')}
              </>
            ) : (
              <>
                <UpOutlined /> {t('hide')}
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

export default IdeaEvaluationDetail;
